import React from 'react';

import { Checkbox } from 'pages/checkout/components/common/checkbox-styled-component';

import { CheckboxOptionWrapper, OptionButton, OptionDescription, OptionTitle } from './Dropdown-styled-components';
import { AutocompleteOption } from './types';

export interface OptionProps {
  option: AutocompleteOption;
  useBoldName?: boolean;
  hoveredOption: number | string | null;
  handleMouseEnter: () => void;
  handleSelect: () => void;
}

const OptionContent = ({
  name,
  description,
  useBoldName,
}: {
  name: string;
  description?: string;
  useBoldName?: boolean;
}) => {
  // If no description is given, use regular text for names
  // unless useBoldName is true
  if (!description) {
    const TextComponent = useBoldName ? OptionTitle : OptionDescription;

    return <TextComponent>{name}</TextComponent>;
  }

  return (
    <div>
      <OptionTitle>{name}</OptionTitle>
      <OptionDescription>{description}</OptionDescription>
    </div>
  );
};

export const Option = ({
  option: { id, name, description, component, isDisabled, isLabel },
  hoveredOption,
  useBoldName,
  handleMouseEnter,
  handleSelect,
}: OptionProps) =>
  component
  || (
    <OptionButton
      tabIndex={-1}
      type="button"
      data-option-id={id} // Used to scroll to option based on ID
      $isHovered={hoveredOption === id}
      disabled={isDisabled}
      $isLabel={Boolean(isLabel)}
      onMouseEnter={handleMouseEnter}
      onClick={handleSelect}
    >
      <OptionContent useBoldName={useBoldName} name={name} description={description} />
    </OptionButton>
  );

interface CheckboxOptionProps extends Omit<OptionProps, 'handleSelect'> {
  isChecked: boolean;
  handleCheck: () => void;
}

export const CheckboxOption = ({
  option: { id, name, description, isDisabled, isLabel },
  isChecked,
  hoveredOption,
  useBoldName,
  handleMouseEnter,
  handleCheck,
}: CheckboxOptionProps) => (
  <CheckboxOptionWrapper
    tabIndex={-1}
    data-option-id={id} // Used to scroll to option based on ID
    $isHovered={hoveredOption === id}
    disabled={isDisabled}
    $isLabel={Boolean(isLabel)}
    onMouseEnter={handleMouseEnter}
    onClick={handleCheck}
  >
    <Checkbox
      name={name}
      checked={isChecked}
    />
    <OptionContent
      useBoldName={useBoldName}
      name={name}
      description={description}
    />
  </CheckboxOptionWrapper>
);
